import helpers from './helpers.js';
import Restable from './restable/restable.js';
import Cacheable from './cacheable/cacheable.js';
import Errorable from './errorable/errorable.js';
import Validatable from './validatable/validatable.js';

/**
 * @ngdoc overview
 * @name BaseClass
 * @description
 *
 * # BaseClass for Models
 * Module containing the skeleton for data models, it loosely follows the active-record pattern. It's based on the Egghead.io Data-Modelling series.
 *
 * The model class is designed to centralize and abstract logic for REST, validation & caching. The `common/base-class` folder holds the logic for these functions.
 *
 * You can auto-generate the boilerplate code for a new model using `$ plop model`
 *
 * ## REST
 *
 * Configure the Model's API endpoint. URL parameters can be bound to instance properties, such as `guid` in this example
 *
 *  ```
 *     User.configureApi('/api/users/:guid', { guid: '@guid' });
 *  ```
 *
 * A Model class has 4 methods of obtaining instances:
 *
 *  ```
 *     //Fetch all users, if they're already cached, don't hit the database
 *     User.query();
 *     //Fetch one user, again if it's cached, don't hit the database
 *     User.get({ guid: '????' });
 *  ```
 *
 * ## REST relationships
 *
 * You can configure 3 types of relationships.
 *
 * ### Belongs To
 *
 * Assign all user instances the prototype function getCompany() which returns an instance of Company,
 * whose guid matches the users companyId
 * ```
 * User.belongsTo('Company', 'companyId', 'guid', '/api/user/:guid/company');
 * user.getCompany().then(company => console.log(company));
 * ```
 * ### Has Many
 *
 * The second parameter can filter out users on a nested data return from the database,
 * i.e.: companies: [{ id: 1, **users: []** }]
 * The URL will replace :properties with own values, and ^properties with foreign values
 * ```
 * Company.hasMany('User', 'users', 'guid', '/api/company/:guid/users/^guid');
 * company.getUser().then(users => console.log(users));
 * ```
 * ### BelongsToMany
 *
 * Since this is not a simple one-to-many relationship, a pivot table is cached on the model Constructor
 * Several actions are preconfigured: query, update detach, attach, create & delete
 *
 * - Query returns instances with a GET
 * - Update will send out a PUT to update pivot information
 * - Attach will add an pre-existing child
 * - Detach will unattach it
 * - Create will create and attach a child
 * - Delete will delete and detach a child
 *
 * You can also customize a response or create custom actions
 * ```
 * User.belongsToMany('Project', 'projects', '/api/users/:guid/projects/^guid', [{
 *     type: 'query',
 *     // Use these handlers if you want to hook into the responses
 *     // (e.g.: manipulate data, perform action)
 *     success: function(projects) { //Append the promise chain with a success handler },
 *     error: function(error) { //Append the promise chain with a failure handler }
 * }, {
 *     type: 'custom',
 *     url: '/api/customUrl',
 *     // Use the fn handler to specify your own behaviour
 *     fn: function(modelName, url, instance, child, pivot) { }
 * }]);
 * user.$queryProject();
 * user.$customProject();
 * ```
*/
/**
 * @ngdoc service
 * @name BaseClass.BaseClass
 * @description
 * Parent service stiching other caching, REST, validation and errors together
 */
export default angular.module('BaseClass', [Errorable, Cacheable, Restable, Validatable])
    .factory('BaseClass', function(BCCacheable, BCRestable, BCValidatable, BCErrorable, $q, $rootScope, $state, $injector) {
        function Base(attributes) {
            var _constructor = this;
            var _prototype = _constructor.prototype;
            helpers.privateVariable(_constructor, 'primaryKey', 'guid');
            $rootScope.$on('logout', () => {
                this.cached.clear();
                this.cachedQueries.clear();
                this.belongsToManyCache.clear();
                this.filterCache = {};
            });
            $rootScope.$on('clearCache', () => {
                this.cached.clear();
                this.cachedQueries.clear();
                this.belongsToManyCache.clear();
                this.filterCache = {};
            });

            /**
             * @ngdoc function
             * @name BaseClass.BaseClass#new
             * @methodOf BaseClass.BaseClass
             * @static
             * @description
             * Use this function instead of `new Model()`. Will properly initialize all model functionality,
             * including caching, REST and relationships.
             *
             * @param {Object} attributes Optional own attributes for model
             * @return {Object} New instance
             */
            _constructor.new = function(attributes) {
                var instance = new _constructor(attributes);
                var savedState = _.cloneDeep(instance);
                Object.defineProperties(instance,{
                    $savedState: {
                        value: savedState,
                        enumerable: false,
                        writable: true,
                        configurable: true
                    },
                    pivot: {
                        writable: true,
                        enumerable: false
                    }
                });
                _constructor.cache(instance);
                helpers.privateVariable(instance, 'belongsToMany', _constructor.createBelongsToManyCache(instance));
                return instance;
            };

            /**
             * @ngdoc method
             * @name BaseClass.BaseClass#query
             * @methodOf BaseClass.BaseClass
             * @static
             * @description
             * Query for instances. Will try cache first if the search parameters have been queried before. If there is a query parameter `as` in the state, proxy the request to `queryAdmin`
             *
             * @param {Object} params Search parameters
             * @param {Boolean} initialise Run data through `Model.new` or return the raw HTTP response, defaults to true
             * @return {Promise} Resolves with array of model(s)
             */
            _constructor.query = function(params, initialise = true) {
                var isCached = _constructor.cachedQueries.isCached(params);
                if(isCached) {
                    var matches = _constructor.cached.where(params);
                    return $q(function(fulfil) { fulfil(matches); });
                }
                return _constructor.queryDB(params,initialise);
            };

            /**
             * @ngdoc method
             * @name BaseClass.BaseClass#queryFilter
             * @methodOf BaseClass.BaseClass
             * @static
             * @description
             * Query for the subset of all instances who satisfy filter criteria
             *
             * It's a convenience method for queryAdmin that auto-selects a company GUID and keeps a cache
             * @example
             * <pre>
             * Model.queryFilter('justtrashed').then(models => {});
             * </pre>
             *
             * @param {String} filter Currenty supported on (some) models: `trashed, justtrashed, past, upcoming`
             * @return {Promise<Array>} Resolves with Array instances
             * @throws {NotInitialised} When API or queryAdmin endpoint is not configured
             */
            this.queryFilter = function(filter = 'upcoming') {
                const Class = this;
                if(Class.filterCache[filter]) {
                    var instances = _.compact(_.map(Class.filterCache[filter], pk => Class.cached[pk]));
                    return $q.resolve(instances);
                }
                return $injector.get('Company').getMe()
                    .then(company => {
                        return Class.queryAdmin({ company: company.guid, status: filter });
                    })
                    .then(instances => {
                        if(filter.indexOf('trashed') >= 0)
                            return instances;

                        Class.filterCache[filter] = [];
                        _.forEach(instances, instance => {
                            Class.cached.cache(instance);
                            Class.filterCache[filter].push(instance[Class.primaryKey]);
                        });
                        return instances;
                    });
            };

            /**
             * @ngdoc method
             * @name BaseClass.BaseClass#get
             * @methodOf BaseClass.BaseClass
             * @description
             * Query for single instance. Will try cache first.
             *
             * @param {Object} params Search parameters
             * @param {Boolean} initialise Run data through `Model.new` or return the raw HTTP response, defaults to true
             * @return {Promise} Resolves with model
             */
            _constructor.get = function(params, initialise) {
                initialise = _.isUndefined(initialise) ? true : initialise;
                var match = _.chain(_constructor.cached.where(params)).first().value();
                if(match && initialise)
                    return $q.resolve(match);
                return _constructor.getDB(params, initialise);
            };

            helpers.extend(_constructor,BCCacheable);
            helpers.include(_constructor,BCCacheable);
            helpers.extend(_constructor,BCRestable);
            helpers.include(_constructor,BCRestable);
            helpers.extend(_constructor, BCErrorable);
            helpers.include(_constructor,BCErrorable);
            helpers.extend(_constructor, BCValidatable);
            helpers.include(_constructor,BCValidatable);
        }

        return Base;
    }).name;
